import { Grid, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useContext, useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import useAPI from "../../../api/hooks/useAPI";
import useAssignUserManagement from "../../../api/hooks/useAssignUserManagement";
import useColors from "../../../api/hooks/useColors";
import { usePOC } from "../../../api/hooks/usePOC";
import { controlKeys, remediationKeys } from "../../../api/queryKeys";
import ExportButton from "../../../components/buttons/ExportButton";
import SettingsButton from "../../../components/buttons/SettingsButton";
import CustomToolTip from "../../../components/CustomTooltip";
import LoadingDialog from "../../../components/dialogs/LoadingDialog";
import CircleOption from "../../../components/filterOption/CircleOption";
import ClearFiltersButton from "../../../components/filters/ClearFiltersButton";
import FilterMenu from "../../../components/filters/FilterMenu";
import SearchFilter from "../../../components/filters/SearchFilter";
import SearchInput from "../../../components/filters/SearchInput";
import LoadingIcon from "../../../components/LoadingIcon";
import LimitsConfirmationDialog from "../../../components/poc/LimitsConfirmationDialog";
import AppPage from "../../../components/structure/AppPage";
import { CustomerFeatureFlagsContext } from "../../../contexts/CustomerFeatureFlagsContext";
import { EnvFeatureFlagContext } from "../../../contexts/EnvFeaturesContext";
import { POCContext } from "../../../contexts/POCContext";
import { useFileNaming } from "../../../hooks/useFileNaming";
import {
  getRemediationKeyFromLabel,
  RemediationTypeChip,
} from "../../../utils/chipsData";
import {
  ControlStatus,
  DRAWER_TABS,
  RemediationStatus,
  RemediationType,
} from "../../../utils/constants";
import { searchObjectProperties } from "../../../utils/search";
import ControlRemediationDrawer from "../../posture/controls/components/ControlRemediationDrawer/ControlRemediationDrawer";
import AutoRemediationHistory from "../../posture/controls/components/ControlRemediationDrawer/remediation/autoRemediation/history/AutoRemediationHistory";
import ScanMenuButton from "../../posture/controls/components/ScanMenuButton";
import IntegrationRowFilter from "../../posture/steps/components/IntegrationRowFilter";
import { IntegrationsMapping } from "../../stack/integrations/IntegrationsMapping";
import PriorityWeights from "./components/PriorityWeights";
import RemediationTable from "./components/RemediationPlanTable";

export default function RemediationPlanView() {
  const { remediationsDemoMode } = useContext(EnvFeatureFlagContext);
  const colors = useColors();
  const { generateFileName } = useFileNaming();
  const [selectedRemediation, setSelectedRemediation] = useState(null);
  const [selectedRowIDs, setSelectedRowIDs] = useState([]);
  const [selectedControl, setSelectedControl] = useState(null);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [isScanLoading, setIsScanLoading] = useState(false);
  const location = useLocation();
  const gridRef = useGridApiRef();
  const { autoRemediationActive } = useContext(CustomerFeatureFlagsContext);

  const [appliedFilters, setAppliedFilters] = useState({
    searchString: "",
    tools: {},
    impact: {},
    feasibility: {},
    priority: {},
    status: {},
    assignee: {},
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const [filteredRemediations, setFilteredRemediations] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    tools: [],
    impact: [],
    feasibility: [],
    priority: [],
    status: [],
    assignee: [],
  });
  const [remediationIdClicked, setRemediationIdClicked] = useState(null);
  const resetSelection = useCallback(() => {
    gridRef.current?.setRowSelectionModel([]);
    setSelectedControl(null);
    setSelectedRemediation(null);
    removeRemediationFromSearchParams();
    setRemediationIdClicked(null);
  }, []);

  const { isPOC } = useContext(POCContext);
  const handleDrawerDisplay = useCallback((shouldDisplay) => {
    setOpenDrawer(shouldDisplay);
  }, []);

  const {
    pocDialogOpen,
    remainingControlsCount,
    dialogMessageFirstLine,
    dialogMessageSecondLine,
    dialogTitle,
    viewedControls,
    controlLoading,
    pocDataLoading,
    handleDialogClose,
    handlePOCClick,
    handlePOCDrawer,
  } = usePOC(handleDrawerDisplay, resetSelection);

  const [open, setOpen] = useState(false);
  const handleSettingsOnClick = () => {
    const message =
      'User tried to click on "Settings" button to view the priority weights but it is disabled.';
    trackEvent(message).then();
  };
  const handleOpen = () => {
    setOpen(true);
    const message =
      'User click on "Settings" button to view the priority weights';
    trackEvent(message).then();
  };
  const handleClose = () => {
    setOpen(false);
    const message = 'User close the "Priority Weights" dialog';
    trackEvent(message).then();
  };
  const { trackEvent, fetchAllRemediations, fetchAllControls } = useAPI();
  const localSearchParams = new URLSearchParams(location.search);
  const [hiddenControlsIds, setHiddenControlsIds] = useState([]);
  const { platformUsers } = useAssignUserManagement();
  const { data: allRemediations, isLoading: isLoadingRemediations } = useQuery({
    queryKey: remediationKeys.all,
    queryFn: fetchAllRemediations,
    select: (response) =>
      response.data.map((remediation) => ({
        id: remediation.remediation_id,
        ...remediation,
      })),
  });

  const { data: controls, isLoading: isLoadingControls } = useQuery({
    queryKey: controlKeys.all,
    queryFn: fetchAllControls,
    select: (data) =>
      data.data.map((control) => ({
        ...control,
        id: control.control_id,
      })),
  });

  // we need both remediations and controls to be loaded before we can display the remediation plan
  const isLoadingData = isLoadingRemediations || isLoadingControls;

  useEffect(() => {
    if (controls?.length > 0 && selectedControl) {
      const control = controls.find(
        (control) => control.control_id === selectedControl.control_id
      );
      setSelectedControl(control);
    }
  }, [controls]);

  const getLabels = (key) => {
    if (RemediationTypeChip[key]) {
      const uniqueLabels = Array.from(
        new Set(
          Object.values(RemediationTypeChip[key]).map((item) => item.label)
        )
      ).reverse();
      return uniqueLabels;
    }
    return [];
  };

  useEffect(() => {
    const pocCondition = (isPOC && !pocDataLoading) || !isPOC;
    if (
      localSearchParams.has("remediation") &&
      !isLoadingData &&
      pocCondition
    ) {
      const remediationId = decodeURIComponent(
        localSearchParams.get("remediation")
      );
      setRemediationIdClicked(remediationId);

      if (isPOC) {
        handlePOCDrawer(remediationId);
      } else {
        setOpenDrawer(true);
      }

      handleDrawer(remediationId);
    }
  }, [location.search, isLoadingData, pocDataLoading]);

  const handleDrawer = (remediationId) => {
    setSelectedRemediation(
      allRemediations?.find(
        (remediation) => remediation.remediation_id.toString() === remediationId
      )
    );
    if (controls) {
      setSelectedControl(
        controls?.find(
          (control) => control.control_id.toString() === remediationId
        )
      );
    }
  };

  const handleRowClick = async (column) => {
    const remediationId = column.row.remediation_id;
    setRemediationIdClicked(remediationId);
    const selectedControl = controls.find(
      (control) => control.control_id.toString() === remediationId
    );

    trackEvent(
      `Click on remediation row with remediation id: ${column?.row?.remediation_id}`
    );

    if (isPOC) {
      handlePOCClick(remediationId);
    } else {
      handleNonPOCClick(column, selectedControl);
    }
  };

  const handleNonPOCClick = (column, selectedControl) => {
    updateSearchParams(column);
  };

  const updateSearchParams = (column) => {
    const params = new URLSearchParams(location.search);
    params.set("remediation", encodeURIComponent(column.row.id));
    setSearchParams(params.toString());
  };

  const removeRemediationFromSearchParams = () => {
    let searchParams = new URLSearchParams(location.search);
    searchParams.delete("remediation");
    setSearchParams(searchParams.toString());
  };

  const onDrawerClose = () => {
    setOpenDrawer(false);
    removeRemediationFromSearchParams();
    setSelectedControl(null);
    setSelectedRemediation(null);
    resetSelection();
  };

  const onRowSelection = (newSelection) => {
    setSelectedRowIDs(newSelection);
  };

  useEffect(() => {
    const impact = getLabels(RemediationType.IMPACT);
    const feasibility = getLabels(RemediationType.FEASIBILITY);
    const priority = getLabels(RemediationType.REMEDIATION_PRIORITY);
    const status = getLabels(RemediationType.STATUS);
    const assignee = platformUsers;
    setFilterOptions((prev) => ({
      ...prev,
      impact,
      feasibility,
      priority,
      status,
      assignee,
    }));
  }, [platformUsers]);

  useEffect(() => {
    setAppliedFilters((prev) => ({
      ...prev,
      status: {
        [RemediationStatus.PARTIALLY_RESOLVED]: true,
        [RemediationStatus.PENDING]: true,
      },
    }));
  }, []);

  // collect snoozed and excluded control ids to hide them from the remediation plan
  useEffect(() => {
    if (!isLoadingControls && controls) {
      const snoozedOrExcludedControlIds = controls
        .filter(
          (control) =>
            control.status === ControlStatus.SNOOZED ||
            control.status === ControlStatus.EXCLUDED
        )
        .map((control) => control.control_id);

      setHiddenControlsIds(snoozedOrExcludedControlIds);
    }
  }, [isLoadingControls, controls]);

  // filter out hidden remediations on when remediations are loaded
  useEffect(() => {
    if (!isLoadingData && allRemediations) {
      const filteredRemediationsWithoutHiddenControls = allRemediations.filter(
        (remediation) => !hiddenControlsIds.includes(remediation.control_id)
      );

      setFilteredRemediations(filteredRemediationsWithoutHiddenControls);

      if (selectedRemediation) {
        setSelectedRemediation((prev) =>
          allRemediations?.find(
            (remediation) => remediation.remediation_id === prev?.remediation_id
          )
        );
      }
    }
  }, [isLoadingData, allRemediations, hiddenControlsIds]);

  // collect tool list for filter
  useEffect(() => {
    if (!isLoadingRemediations && allRemediations) {
      const tools = [];

      allRemediations?.forEach((remediation) => {
        if (remediation.tool && !tools.includes(remediation.tool)) {
          tools.push(remediation.tool);
        }
      });

      tools.sort();

      setFilterOptions((prev) => ({
        ...prev,
        tools: ["All", ...tools.sort()],
      }));
    }
  }, [isLoadingRemediations, allRemediations]);

  useEffect(() => {
    if (allRemediations && selectedRemediation) {
      setSelectedRemediation((prev) =>
        allRemediations?.find(
          (remediation) => remediation.remediation_id === prev?.remediation_id
        )
      );
    }
  }, [allRemediations]);

  //filter the remediations based on the applied filters
  useEffect(() => {
    if (allRemediations) {
      let newFilters = { ...appliedFilters };
      let message =
        "User applied filters on remediation plan: " +
        JSON.stringify(newFilters);
      trackEvent(message).then();

      let filtered = allRemediations;
      if (Object.keys(appliedFilters.tools).length > 0) {
        const tools_filter = Object.keys(appliedFilters.tools).filter(
          (key) => appliedFilters.tools[key] === true
        );
        if (tools_filter.length > 0) {
          filtered = filtered.filter((remediation) =>
            tools_filter.includes(remediation.tool)
          );
        }
      }
      if (Object.keys(appliedFilters.impact).length > 0) {
        const impact_filter = Object.keys(appliedFilters.impact).filter(
          (key) => appliedFilters.impact[key] === true
        );
        if (impact_filter.length > 0) {
          filtered = filtered.filter((remediation) =>
            impact_filter.includes(remediation.impact.toString())
          );
        }
      }
      if (Object.keys(appliedFilters.feasibility).length > 0) {
        const feasibility_filter = Object.keys(
          appliedFilters.feasibility
        ).filter((key) => appliedFilters.feasibility[key] === true);
        if (feasibility_filter.length > 0) {
          filtered = filtered.filter((remediation) => {
            return feasibility_filter.includes(
              remediation.feasibility.toString()
            );
          });
        }
      }
      if (Object.keys(appliedFilters.priority).length > 0) {
        const priority_filter = Object.keys(appliedFilters.priority).filter(
          (key) => appliedFilters.priority[key] === true
        );
        if (priority_filter.length > 0) {
          filtered = filtered.filter((remediation) =>
            priority_filter.includes(
              // if custom priority is set, use it, otherwise use the priority
              remediation.custom_priority
                ? remediation.custom_priority?.toString()
                : remediation.priority?.toString()
            )
          );
        }
      }
      if (Object.keys(appliedFilters.status).length > 0) {
        const status_filter = Object.keys(appliedFilters.status).filter(
          (key) => {
            return appliedFilters.status[key] === true;
          }
        );
        if (status_filter.length > 0) {
          filtered = filtered.filter((remediation) => {
            return status_filter.includes(remediation.status);
          });
        }
      }
      if (appliedFilters.searchString !== "") {
        const searchString = appliedFilters.searchString;

        const parsedNumber = Number(searchString);
        const isNumber = !isNaN(parsedNumber);

        filtered = filtered.filter((row) => {
          if (isNumber) {
            return row.id && row.id.toString().includes(searchString);
          } else {
            return searchObjectProperties(row, searchString.toLowerCase());
          }
        });
      }

      if (Object.keys(appliedFilters.assignee).length > 0) {
        const assignee_filter = Object.keys(appliedFilters.assignee).filter(
          (key) => appliedFilters.assignee[key] === true
        );
        if (assignee_filter.length > 0) {
          filtered = filtered.filter((remediation) =>
            assignee_filter.includes(remediation.assignee_email)
          );
        }
      }
      // filter out hidden remediations when filters are applied
      filtered = filtered.filter(
        (remediation) => !hiddenControlsIds.includes(remediation.control_id)
      );

      setFilteredRemediations(filtered);
    }
  }, [
    allRemediations,
    hiddenControlsIds,
    appliedFilters.tools,
    appliedFilters.impact,
    appliedFilters.feasibility,
    appliedFilters.priority,
    appliedFilters.searchString,
    appliedFilters.status,
    appliedFilters.assignee,
  ]);

  const handleTableExport = () => {
    const message =
      'User click on "Export" button to download the "remediation plan" table';
    trackEvent(message).then();

    const csvExportOptions = {
      fileName: generateFileName("remediation_plan"),
    };
    gridRef?.current?.exportDataAsCsv(csvExportOptions);
  };

  return (
    <AppPage title="Remediation Plan">
      {isLoadingData ? (
        <LoadingIcon />
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Grid container justifyContent={"space-between"} gap={2}>
              <Grid item xs={12}>
                <Grid container>
                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    gap={1}
                    alignItems={"center"}
                  >
                    {/* TOOL FILTER */}
                    <SearchFilter
                      filterName={"Tool"}
                      fieldName={"tools"}
                      options={filterOptions.tools.map((tool) => {
                        if (tool === "All") {
                          return {
                            name: tool,
                            label: (
                              <Typography variant="caption">
                                All Tools
                              </Typography>
                            ),
                            displayName: tool,
                          };
                        } else
                          return {
                            name: tool,
                            label: <IntegrationRowFilter name={tool} />,
                            displayName: IntegrationsMapping[tool].displayName,
                          };
                      })}
                      appliedFilters={appliedFilters}
                      setAppliedFilters={setAppliedFilters}
                      fullOptions={filterOptions.tools}
                    />
                    {/* PRIORITY FILTER */}
                    <FilterMenu
                      filterName={"Priority"}
                      fieldName={"priority"}
                      options={filterOptions.priority.map((priority) => {
                        return {
                          name: getRemediationKeyFromLabel(
                            RemediationType.REMEDIATION_PRIORITY,
                            priority
                          ),
                          label: (
                            <CircleOption label={priority} type="priority" />
                          ),
                          displayName: priority,
                        };
                      })}
                      appliedFilters={appliedFilters}
                      setAppliedFilters={setAppliedFilters}
                      fullOptions={filterOptions.priority}
                    />
                    {/* FEASIBILITY FILTER */}
                    <FilterMenu
                      filterName={"Feasibility"}
                      fieldName={"feasibility"}
                      options={filterOptions.feasibility.map((feasibility) => {
                        return {
                          name: getRemediationKeyFromLabel(
                            RemediationType.FEASIBILITY,
                            feasibility
                          ),
                          label: (
                            <CircleOption
                              label={feasibility}
                              type="feasibility"
                            />
                          ),
                          displayName: feasibility,
                        };
                      })}
                      appliedFilters={appliedFilters}
                      setAppliedFilters={setAppliedFilters}
                      fullOptions={filterOptions.feasibility}
                    />
                    {/* IMPACT FILTER */}
                    <FilterMenu
                      filterName={"Impact"}
                      fieldName={"impact"}
                      options={filterOptions.impact.map((impact) => {
                        return {
                          name: getRemediationKeyFromLabel(
                            RemediationType.IMPACT,
                            impact
                          ),
                          label: <CircleOption label={impact} type="impact" />,
                          displayName: impact,
                        };
                      })}
                      appliedFilters={appliedFilters}
                      setAppliedFilters={setAppliedFilters}
                      fullOptions={filterOptions.impact}
                    />
                    {/* STATUS FILTER */}
                    <FilterMenu
                      filterName={"Status"}
                      fieldName={"status"}
                      options={filterOptions.status.map((status) => {
                        return {
                          name: status,
                          label: <CircleOption label={status} type="status" />,
                          displayName: status,
                        };
                      })}
                      appliedFilters={appliedFilters}
                      setAppliedFilters={setAppliedFilters}
                      fullOptions={filterOptions.status}
                    />
                    {/* ASSIGNEE FILTER */}
                    <SearchFilter
                      filterName={"Assignee"}
                      fieldName={"assignee"}
                      options={filterOptions.assignee.map((assignee) => {
                        return {
                          name: assignee.email,
                          label: (
                            <Box display={"flex"} alignItems={"center"} gap={1}>
                              <Box
                                sx={{
                                  minWidth: "16px",
                                  minHeight: "16px",
                                  borderRadius: "50%",
                                  backgroundColor: colors.primary_main,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "10px",
                                  color: colors.common_white,
                                }}
                              >
                                {assignee.display_name
                                  ? assignee.display_name
                                      .charAt(0)
                                      .toUpperCase()
                                  : assignee.email.charAt(0).toUpperCase()}
                              </Box>
                              <Typography variant="caption" noWrap>
                                {assignee.display_name || assignee.email}
                              </Typography>
                            </Box>
                          ),
                          displayName: assignee.display_name,
                        };
                      })}
                      appliedFilters={appliedFilters}
                      setAppliedFilters={setAppliedFilters}
                      fullOptions={filterOptions.assignee}
                    />
                    <ClearFiltersButton
                      appliedFilters={appliedFilters}
                      setAppliedFilters={setAppliedFilters}
                    />
                  </Box>
                </Grid>
              </Grid>
              <Grid item xs={3}>
                <Grid item>
                  <SearchInput
                    fieldName={"searchString"}
                    appliedFilters={appliedFilters}
                    setAppliedFilters={setAppliedFilters}
                  />
                </Grid>
              </Grid>
              <Grid
                item
                gap={1}
                display={"flex"}
                direction={"row-reverse"}
                onClick={handleSettingsOnClick}
              >
                {remediationsDemoMode && (
                  <CustomToolTip
                    title="Customize Priority Weights"
                    disableHoverListener={!remediationsDemoMode}
                  >
                    <SettingsButton
                      onClick={handleOpen}
                      disabled={!remediationsDemoMode}
                    />
                  </CustomToolTip>
                )}
                <ExportButton
                  sx={{
                    border: "1px solid " + colors.grey_48percent,
                  }}
                  handleExport={handleTableExport}
                />
                <ScanMenuButton
                  isScanLoading={isScanLoading}
                  setLoading={setIsScanLoading}
                  loadingMessage={
                    "Analyzing systems for selected remediations..."
                  }
                  gapIdsToScan={selectedRowIDs}
                  gapIdsToSnooze={[]}
                  showLabel={true}
                />
              </Grid>
            </Grid>
            <RemediationTable
              loading={isLoadingData}
              remediations={filteredRemediations}
              onRowClick={handleRowClick}
              onRowSelection={onRowSelection}
              selectedRowIDs={selectedRowIDs}
              refToPass={gridRef}
              viewedControls={viewedControls}
              controls={controls}
            />
            <Grid item>
              {autoRemediationActive && !isLoadingData && (
                <AutoRemediationHistory remediations={allRemediations} />
              )}
            </Grid>
            <ControlRemediationDrawer
              open={openDrawer}
              onClose={() => {
                onDrawerClose();
                gridRef.current?.setRowSelectionModel([]);
              }}
              selectedTab={DRAWER_TABS.REMEDIATION_TAB}
              selectedControl={selectedControl}
            />
          </Box>
          <PriorityWeights open={open} handleClose={handleClose} />
          <LimitsConfirmationDialog
            dialogOpen={pocDialogOpen}
            handleDialogClose={handleDialogClose}
            limitsReached={remainingControlsCount === 0}
            dialogMessageFirstLine={dialogMessageFirstLine}
            dialogMessageSecondLine={dialogMessageSecondLine}
            controlId={remediationIdClicked}
            title={dialogTitle}
          />
          <LoadingDialog
            message="Loading remediation..."
            open={controlLoading}
          />
        </>
      )}
    </AppPage>
  );
}
